import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import {
  Badge, Banner, EmptyState, PageLoading, SearchInput, Select,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import AddUserDialog from './AddUserDialog';
import UserDetailDialog from './UserDetailDialog';
import {
  ROLE_LABEL, STATUS_LABEL, STATUS_TONE, initialsOf, isAdminRole,
} from './roleMeta';
import type {
  ManagerOption, Role, TeamMember, TeamSummary, UserStatus,
} from '../../types';

export default function TeamPage() {
  const { user: me, can } = useAuth();
  const toast = useToast();
  const canMakeAdmins = can('company_admin', 'platform_admin');

  const [people, setPeople] = useState<TeamMember[] | null>(null);
  const [summary, setSummary] = useState<TeamSummary | null>(null);
  const [managers, setManagers] = useState<ManagerOption[]>([]);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'' | Role>('');
  const [statusFilter, setStatusFilter] = useState<'' | UserStatus>('');

  const [adding, setAdding] = useState<Role | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, stats, mgrs] = await Promise.all([
        api.get<TeamMember[]>('/users'),
        api.get<TeamSummary>('/users/summary'),
        api.get<ManagerOption[]>('/users/managers'),
      ]);
      setPeople(list);
      setSummary(stats);
      setManagers(mgrs);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load the team.');
      setPeople([]);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (people ?? [])
      .filter((p) => !roleFilter || p.role === roleFilter)
      .filter((p) => !statusFilter || p.status === statusFilter)
      .filter((p) => !needle
        || p.fullName.toLowerCase().includes(needle)
        || p.email?.toLowerCase().includes(needle)
        || p.mobile?.includes(needle)
        || p.employeeCode?.toLowerCase().includes(needle));
  }, [people, search, roleFilter, statusFilter]);

  const notJoined = (people ?? []).filter((p) => p.pendingInvitationId);
  const filtered = Boolean(search || roleFilter || statusFilter);
  const noSeatsLeft = summary?.seatsLeft === 0;

  return (
    <>
      <PageHeader
        title="Team"
        subtitle="Admins run this console. Salesmen work the field on the app."
        actions={(
          <>
            {canMakeAdmins ? (
              <button
                type="button"
                className="btn btn-secondary"
                data-testid="add-admin"
                disabled={noSeatsLeft}
                onClick={() => setAdding('company_admin')}
              >
                <Icon name="plus" size={16} /> Add admin
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              data-testid="add-salesman"
              disabled={noSeatsLeft}
              onClick={() => setAdding('sales_rep')}
            >
              <Icon name="plus" size={16} /> Add salesman
            </button>
          </>
        )}
      />

      <div className="page">
        {summary ? (
          <div className="stat-row">
            <Stat label="Admins" value={summary.byRole.admins} icon="sliders" />
            <Stat label="Managers" value={summary.byRole.managers} icon="users" />
            <Stat label="Salesmen" value={summary.byRole.salesmen} icon="map" />
            <Stat
              label={summary.maxUsers == null ? 'Seats used' : 'Seats'}
              value={summary.maxUsers == null
                ? String(summary.used)
                : `${summary.used} / ${summary.maxUsers}`}
              icon="check"
              tone={noSeatsLeft ? 'danger' : undefined}
            />
          </div>
        ) : null}

        {noSeatsLeft ? (
          <div style={{ marginBottom: 16 }}>
            <Banner kind="warning">
              <strong>Every seat on your plan is taken.</strong>{' '}
              Remove somebody who has left, or upgrade to add more of your team.
            </Banner>
          </div>
        ) : null}

        {notJoined.length ? (
          <div style={{ marginBottom: 16 }}>
            <Banner kind="info">
              {notJoined.length === 1
                ? `${notJoined[0]?.fullName} has been added but has not joined yet.`
                : `${notJoined.length} people have been added but have not joined yet.`}{' '}
              Open them to send the invitation again.
            </Banner>
          </div>
        ) : null}

        <div className="toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name, number or code" />

          <div style={{ width: 170 }}>
            <Select
              value={roleFilter}
              aria-label="Role"
              onChange={(e) => setRoleFilter(e.target.value as '' | Role)}
            >
              <option value="">All roles</option>
              <option value="company_admin">Admins</option>
              <option value="manager">Managers</option>
              <option value="sales_rep">Salesmen</option>
            </Select>
          </div>

          <div style={{ width: 180 }}>
            <Select
              value={statusFilter}
              aria-label="Status"
              onChange={(e) => setStatusFilter(e.target.value as '' | UserStatus)}
            >
              <option value="">Any status</option>
              <option value="active">Active</option>
              <option value="invited">Not joined yet</option>
              <option value="suspended">Suspended</option>
            </Select>
          </div>

          {filtered ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => { setSearch(''); setRoleFilter(''); setStatusFilter(''); }}
            >
              <Icon name="x" size={14} /> Clear
            </button>
          ) : null}
        </div>

        <div className="card">
          {!people ? (
            <div className="card-pad"><PageLoading /></div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon="users"
              title={filtered ? 'Nobody matches those filters' : 'Just you so far'}
              body={filtered
                ? 'Try clearing a filter, or search for part of a name.'
                : 'Add your field team so they can start marking attendance and adding customers.'}
              action={!filtered ? (
                <button type="button" className="btn btn-primary" onClick={() => setAdding('sales_rep')}>
                  <Icon name="plus" size={16} /> Add salesman
                </button>
              ) : undefined}
            />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Contact</th>
                    <th>Reports to</th>
                    <th>Status</th>
                    <th>Last signed in</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((person) => (
                    <tr
                      key={person.id}
                      className="clickable"
                      data-user-row={person.id}
                      onClick={() => setOpenId(person.id)}
                    >
                      <td className="primary">
                        <div className="row gap-8">
                          <span className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                            {initialsOf(person.fullName)}
                          </span>
                          <span style={{ minWidth: 0 }}>
                            {person.fullName}
                            {person.id === me?.id ? <span className="faint small"> (you)</span> : null}
                            {person.designation ? (
                              <div className="small faint">{person.designation}</div>
                            ) : null}
                          </span>
                        </div>
                      </td>
                      <td>
                        <Badge tone={isAdminRole(person.role) ? 'brand' : 'neutral'}>
                          {ROLE_LABEL[person.role]}
                        </Badge>
                      </td>
                      <td className="small">
                        {/* The one that matters for signing in comes first. */}
                        {isAdminRole(person.role)
                          ? person.email || <span className="faint">No email</span>
                          : person.mobile || <span className="faint">No number</span>}
                      </td>
                      <td>{person.reportsTo?.fullName ?? <span className="faint">—</span>}</td>
                      <td>
                        <Badge tone={STATUS_TONE[person.status as UserStatus] ?? 'neutral'}>
                          {STATUS_LABEL[person.status as UserStatus] ?? person.status}
                        </Badge>
                      </td>
                      <td className="nowrap small">
                        {person.lastLoginAt
                          ? new Date(person.lastLoginAt).toLocaleDateString()
                          : <span className="faint">Never</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {adding ? (
        <AddUserDialog
          initialRole={adding}
          managers={managers}
          onClose={() => setAdding(null)}
          onCreated={() => { setAdding(null); void load(); }}
        />
      ) : null}

      {openId ? (
        <UserDetailDialog
          userId={openId}
          managers={managers}
          onClose={() => setOpenId(null)}
          onChanged={() => void load()}
        />
      ) : null}
    </>
  );
}

function Stat({
  label, value, icon, tone,
}: {
  label: string; value: number | string;
  icon: 'sliders' | 'users' | 'map' | 'check';
  tone?: 'danger';
}) {
  return (
    <div className="stat">
      <span className="stat-icon"><Icon name={icon} size={16} /></span>
      <span>
        <span className={`stat-value${tone === 'danger' ? ' danger' : ''}`}>{value}</span>
        <span className="stat-label">{label}</span>
      </span>
    </div>
  );
}
